import React, { Suspense, useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CloseFullscreen, OpenInFull } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const OpportunityNotesWysiwygEditor = React.lazy(() => import('./OpportunityNotesWysiwygEditor'));

export type OpportunityNotesVariant = 'compact' | 'expanded';

type OpportunityNotesFieldProps = {
  notes: string;
  onNotesChange: (value: string) => void;
  variant?: OpportunityNotesVariant;
  author?: string;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
};

const OpportunityNotesField: React.FC<OpportunityNotesFieldProps> = ({
  notes,
  onNotesChange,
  variant = 'compact',
  author = '',
  fullscreen = false,
  onToggleFullscreen,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [editing, setEditing] = useState(false);
  const [editSeed, setEditSeed] = useState('');
  const expanded = variant === 'expanded' || fullscreen;
  const threadMaxHeight = fullscreen ? undefined : expanded ? '50vh' : '220px';
  const editorMaxHeight = fullscreen ? undefined : expanded ? '50vh' : '280px';
  const minHeight = fullscreen ? 280 : expanded ? 200 : 88;

  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const panelBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const muted = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';

  const startEdit = () => {
    setEditSeed(notes);
    setEditing(true);
  };

  const markdownSx = {
    color: textPrimary,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: textPrimary,
      fontWeight: 600,
      mt: 1.25,
      mb: 0.5,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    '& h1': { fontSize: '1.15rem' },
    '& h2': { fontSize: '1.05rem' },
    '& h3': { fontSize: '0.95rem' },
    '& p': {
      my: 0.5,
      lineHeight: 1.65,
      fontSize: '0.875rem',
    },
    '& ul, & ol': {
      pl: 2.5,
      my: 0.5,
      '& li': {
        fontSize: '0.875rem',
        lineHeight: 1.65,
        mb: 0.25,
      },
    },
    '& pre': {
      bgcolor: muted,
      border: `1px solid ${panelBorder}`,
      borderRadius: 1,
      p: 1.25,
      my: 1,
      overflowX: 'auto',
      '& code': {
        fontFamily: '"Roboto Mono", monospace',
        fontSize: '0.8rem',
        bgcolor: 'transparent',
        p: 0,
      },
    },
    '& :not(pre) > code': {
      bgcolor: muted,
      px: '5px',
      py: '1px',
      borderRadius: 0.5,
      fontFamily: '"Roboto Mono", monospace',
      fontSize: '0.82em',
    },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.secondary.main}`,
      pl: 1.5,
      ml: 0,
      my: 1,
      color: textSecondary,
      fontStyle: 'italic',
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      my: 1,
      '& th, & td': {
        border: `1px solid ${panelBorder}`,
        px: 1,
        py: 0.5,
        fontSize: '0.82rem',
        textAlign: 'left',
      },
      '& th': { bgcolor: muted, fontWeight: 600 },
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${panelBorder}`,
      my: 1.25,
    },
    '& a': { color: theme.palette.secondary.main },
    '& strong': { fontWeight: 600 },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        width: '100%',
        ...(fullscreen ? { flex: 1, minHeight: 0, height: '100%' } : null),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Notes
        </Typography>
        {onToggleFullscreen ? (
          <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Show notes fullscreen'}>
            <IconButton
              size="small"
              onClick={onToggleFullscreen}
              aria-label={fullscreen ? 'Exit fullscreen' : 'Show notes fullscreen'}
            >
              {fullscreen ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      {editing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: fullscreen ? 1 : undefined, minHeight: 0 }}>
          <Suspense
            fallback={(
              <Box
                sx={{
                  border: `1px solid ${panelBorder}`,
                  borderRadius: 1,
                  bgcolor: panelBg,
                  minHeight,
                  flex: fullscreen ? 1 : undefined,
                }}
              />
            )}
          >
            <OpportunityNotesWysiwygEditor
              initialMarkdown={editSeed}
              author={author}
              onNotesChange={onNotesChange}
              fillHeight={fullscreen}
              minHeight={minHeight}
              maxHeight={editorMaxHeight}
              markdownSx={markdownSx}
              panelBorder={panelBorder}
              panelBg={panelBg}
            />
          </Suspense>
          <Typography variant="caption" color="text.secondary">
            Type at the top to add an entry — your name and UTC time are added then.
            Select text and right-click to format. Changes save with the lead.
          </Typography>
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={startEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEdit();
            }
          }}
          sx={{
            border: `1px solid ${panelBorder}`,
            borderRadius: 1,
            bgcolor: panelBg,
            px: 1.5,
            py: 1.25,
            minHeight,
            maxHeight: threadMaxHeight,
            overflowY: 'auto',
            cursor: 'text',
            flex: fullscreen ? 1 : undefined,
            '&:hover': {
              borderColor: theme.palette.secondary.main,
            },
          }}
        >
          {notes.trim() ? (
            <Box sx={markdownSx}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ children, ...props }) => (
                    <a
                      {...props}
                      onClick={(event) => event.stopPropagation()}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {notes}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Click to add notes — type at the top to stamp your name and UTC time
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default OpportunityNotesField;
